'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Button } from '@extern/ui/button';
import { Text } from '@extern/ui/text';
import Image from 'next/image';
import AlicePhoto from '@/public/images/welcome-back/alice.png';
import JulianaPhoto from '@/public/images/welcome-back/juliana.png';
import VictoriaPhoto from '@/public/images/welcome-back/victoria.png';

/**
 * The in-sprint trial bar — pinned to the bottom of every Labs sprint screen for an unpaid learner.
 *
 * Purpose: the pre-paywall hand-off only asks once, at the very end, so it can only convert learners
 * who FINISH. The abandon-point funnel says most do not. This bar carries the same ask through the
 * whole sprint, which is where the trial-start primary actually has room to move.
 *
 * ⚠️ VISUAL + CLAIM ARE MIRRORED FROM `features/payment/_partials/trial-bottom-bar.tsx` — deliberately
 * the same bar the learner already met on `/payment`, so it reads as continuity rather than a new
 * upsell. It is a SEPARATE component rather than a shared import for one reason: that one fires
 * `PAYMENT_EVENTS.TRIAL_BAR_CLICKED` through `usePaymentAnalytics`, which does NOT stamp `arm`. An
 * unstamped event on an experiment surface is invisible to the causal read, so this surface has to go
 * through `useLabsAnalytics` instead. The cost of that choice is duplicated copy: **if the "3/4 of
 * externs" claim ever changes it must change in BOTH files.**
 *
 * ⚠️ NEITHER `fixed` NOR `sticky` — a plain, static flex row. `labs-flow.tsx` makes the sprint a flex
 * column of [scrollable screen | this bar], so the bar cannot overlap anything by construction. Both
 * overlay approaches were tried first and MEASURED to fail across 16 screens x 4 viewports:
 *   - `fixed` + bottom padding: padding cannot help on a screen taller than the viewport, because
 *     content sits behind the bar at the top-of-page scroll position — exactly where the flow puts the
 *     learner (`window.scrollTo(0, 0)` on every step change).
 *   - `sticky bottom-0`: while stuck it paints over earlier content just like `fixed`. It only reserves
 *     space at its natural position, which is no help until you have already scrolled to the end.
 * Between them they hid M1's Back/Skip, m1-intro / m1-results / m2-activity's Back/Continue,
 * m3-reading's Back/Continue, m3-activity's answer buttons, m4-selection's first card and m4-activity's
 * "Run analysis", depending on viewport. Do not "simplify" this to a positioned element.
 *
 * Presentational only — no flag read, no analytics, no router. The wiring lives in `labs-flow.tsx` so
 * this renders in Storybook without Clerk, PostHog or a resolved arm.
 */

const TRIAL_BAR_AVATARS = [{
  alt: 'Juliana Phillips-Acie',
  src: JulianaPhoto
}, {
  alt: 'Victoria Nguyen',
  src: VictoriaPhoto
}, {
  alt: 'Alice Mollet',
  src: AlicePhoto
}] as const;

/** Overlap between stacked avatars, in px. Negative margin, so the three read as a cluster. */
const AVATAR_OVERLAP = -12;

/**
 * `ctaEnabled` is false only while the experiment arm is still resolving, so a tap cannot produce a
 * conversion with no arm stamped on it. The bar itself always renders once the sprint reserves its row —
 * hiding it on readiness changed the screen's ancestry and remounted it mid-interaction. If the flags
 * never resolve the hook releases the CTA anyway after a grace period: an unmeasured conversion beats no
 * conversion.
 */
export function LabsTrialBar(t0) {
  const $ = _c(7);
  const {
    ctaEnabled: t1,
    onStartTrial
  } = t0;
  const ctaEnabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex shrink-0 items-center">{TRIAL_BAR_AVATARS.map(_temp)}</div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-role-info">3/4</span>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex w-full flex-row-reverse items-center gap-4 md:w-auto md:flex-row">{t2}<Text className="text-[16px] leading-5 md:text-[18px] md:leading-6">{t3} of externs{" "}<span className="text-role-info">find a job outcome</span> within 1 year</Text></div>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = !ctaEnabled;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      initial: "sm",
      md: "md"
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== onStartTrial || $[5] !== t5) {
    t7 = <div className="w-full shrink-0 border-role-outline-subtle border-t bg-role-background" data-testid="labs-trial-bar"><div className="mx-auto flex w-full max-w-[1060px] flex-col items-center gap-4 px-4 py-4 md:flex-row md:justify-between md:gap-6 md:px-0">{t4}<Button className="w-full md:w-auto" color="info" disabled={t5} onClick={onStartTrial} rounded="full" size={t6} type="button">Start $0 trial</Button></div></div>;
    $[4] = onStartTrial;
    $[5] = t5;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  return t7;
}
function _temp(avatar, index) {
  return <div className="relative h-10 w-10 overflow-hidden rounded-full border-2 border-white bg-role-surface" key={avatar.alt} style={{
    marginLeft: index === 0 ? 0 : AVATAR_OVERLAP,
    zIndex: TRIAL_BAR_AVATARS.length - index
  }}><Image alt={avatar.alt} className="object-cover" fill={true} sizes="40px" src={avatar.src} /></div>;
}